import { forwardRef, useContext } from 'react';
import { Field, FastField } from 'formik';
import { fieldToTextField } from 'formik-mui';
import { TextField as MuiTextField } from '@mui/material';
import { AutosaveContext } from '../common/AutosaveContext';
import { LengthLabel } from './LengthLabel';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';

export interface TextFieldProps {
  readonly forceDisableAutosave?: boolean;
  readonly status?: boolean;
  readonly displayMaxLength?: boolean;
  readonly maxLength?: number;
  readonly useFastField?: boolean;
  readonly forceHelperText?: boolean;
}

export const TextField = forwardRef<HTMLDivElement, TextFieldProps & MuiTextFieldProps>(({
  id, name, label,
  required = false,
  helperText = null,
  forceDisableAutosave = false,
  onChange = () => {},
  InputProps = {},
  displayMaxLength = true,
  maxLength = null,
  useFastField = false,
  forceHelperText = false,
  ...restProps
}, ref) => {
  const [autosave] = useContext(AutosaveContext);
  const FieldComponent = useFastField ? FastField : Field;

  return (
    <FieldComponent
      name={name}
    >
      {(props) => {
        const hasError = props.form.errors && props.form.errors[name];
        const isTouched = props.form.touched && props.form.touched[name];

        const displayHelperText = (hasError && isTouched) || Boolean(helperText) || (maxLength > 0 && displayMaxLength);

        return (
          <MuiTextField
            {...fieldToTextField(props)}
            {...restProps}
            required={required}
            id={id || name}
            inputRef={ref}
            label={label}
            helperText={displayHelperText ? (
              <>
                {hasError && isTouched && !forceHelperText ? props.form.errors[name] : helperText}

                {maxLength > 0 && displayMaxLength && (
                  <LengthLabel
                    value={props.field.value}
                    maxLength={maxLength}
                    hasLabel={Boolean(label)}
                  />
                )}
              </>
            ) : null}
            onBlur={() => {
              if (autosave && !forceDisableAutosave) {
                autosave();
              }
            }}
            onChange={(e) => {
              props.field.onChange(e);
              onChange(e);
            }}
            InputProps={{
              inputProps: { maxLength },
              ...InputProps,
            }}
          />
        );
      }}
    </FieldComponent>
  );
});
